import { useEffect, useRef } from "react";
import type { ChannelMessage } from "@/lib/types/channel";
import type {
  MessagesAction,
  MessagesStateWithCursor,
} from "../store/messages-reducer";

const POLL_INTERVAL_MS = 10_000;

type PollFetcher = () => Promise<{ messages: ChannelMessage[] }>;

/**
 * Shared polling engine used by usePollMessages (channels) and
 * useConversationPollMessages (DMs). Fetches on a 10s interval,
 * dispatches APPEND_BATCH for any messages not already in state, and
 * reconciles the reply count of messages it already knows about.
 *
 * The fetcher is held in a ref so it can safely capture the latest
 * channelId/conversationId without restarting the interval.
 */
export function usePollMessagesCore(
  fetcher: PollFetcher,
  stableKey: string,
  state: MessagesStateWithCursor,
  dispatch: React.Dispatch<MessagesAction>,
  enabled = true,
): void {
  const fetcherRef = useRef<PollFetcher>(fetcher);
  fetcherRef.current = fetcher;

  // The interval closes over its first render, so read live state via a ref.
  const stateRef = useRef(state);

  const knownIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    stateRef.current = state;
    for (const m of state.messages) {
      knownIdsRef.current.add(m.id);
    }
  }, [state]);

  useEffect(() => {
    if (!enabled) return;

    const poll = async (): Promise<void> => {
      try {
        const result = await fetcherRef.current();

        // Refresh reply counts on messages we already hold. Without this the
        // count is frozen at whatever the initial render returned, so a reply
        // added or deleted elsewhere never shows up. The open thread is skipped
        // because ThreadPanel owns its parent's count while mounted, and its
        // optimistic replies land before the server can report them.
        const { messages: localMessages, threadParent } = stateRef.current;
        for (const incoming of result.messages) {
          if (!knownIdsRef.current.has(incoming.id)) continue;
          if (threadParent?.id === incoming.id) continue;
          const local = localMessages.find((m) => m.id === incoming.id);
          if (local && local._count.replies !== incoming._count.replies) {
            dispatch({
              type: "PATCH",
              messageId: incoming.id,
              patch: { _count: incoming._count },
            });
          }
        }

        const newMessages = result.messages.filter(
          (m) => !knownIdsRef.current.has(m.id),
        );
        if (newMessages.length > 0) {
          for (const m of newMessages) knownIdsRef.current.add(m.id);
          dispatch({
            type: "APPEND_BATCH",
            messages: newMessages.map((m) => ({
              ...m,
              optimisticStatus: "sent" as const,
            })),
          });
        }
      } catch {
        // silently ignore transient poll errors
      }
    };

    // Poll once on mount: the server-rendered messages that seed state can be a
    // replayed RSC payload from Next's router cache, so their reply counts may
    // already be stale by the time the view opens.
    void poll();
    const interval = setInterval(poll, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [stableKey, dispatch, enabled]);
}
